import { useState, useEffect } from "react";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TagOutlinedIcon from '@mui/icons-material/TagOutlined';
import { Box, Typography, colors } from '@mui/material';
import { Button, Select, MenuItem, FormControl, CircularProgress } from '@mui/material';

import axios from "axios";
import { toast } from 'react-toastify';


import Auth, { API_URL } from "../../../../utils/auth";

const SlackModal = ({ onClose }) => {

  const [manageLoading, setManageLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [slackWorkspace, setSlackWorkspace] = useState(null);
  const [slackChannels, setSlackChannels] = useState([]);

  // State for notification toggles
  const DEFAULT_SETTINGS = {
    clientHealthAlerts: false,
    taskReminders: false,
    morningBriefing: false,
    weekInReview: false,
    frequency: 'realtime'
  };

  const [notificationSettings, setNotificationSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    loadSlackSettings();
  }, []);


  const closeDialog = () => {
    setSlackWorkspace(null);
    setSlackChannels([]);
    onClose();
  };

  const isEmpty = (obj) => {
    if (obj == null || typeof obj !== 'object') return true;
    return Object.keys(obj).length === 0;
  }

  const loadSlackSettings = async () => {
    setManageLoading(true);
    await axios
      .get(`${API_URL}/slack/workspace-info`, {
        headers: {
          Authorization: `Bearer ${Auth.getToken()}`,
        },
      })
      .then((response) => {
        setSlackWorkspace(response.data.workspace);
        setSlackChannels(response.data.channels);
        setNotificationSettings(isEmpty(response.data.notificationSettings) ? DEFAULT_SETTINGS : response.data.notificationSettings);
      })
      .catch((err) => {
        toast.error(`Failed to fetch Slack workspace info: ${err.message}`);
      })
      .finally(() => setManageLoading(false));
  };

  const handleToggle = (key, value) => setNotificationSettings(prev => ({ ...prev, [key]: (value !== undefined ? value : !prev[key]) }));

  const updateNotificationSettings = () => {
    setUpdateLoading(true);
    axios
      .post(
        `${API_URL}/slack/notification-channels`,
        notificationSettings,
        {
          headers: {
            Authorization: `Bearer ${Auth.getToken()}`,
          },
        }
      )
      .then(() => {
        toast.success('Notification settings has been updated successfully!');
        closeDialog();
      })
      .catch((err) => {
        toast.error('Failed to update notification settings!');
        console.error(err);
      })
      .finally(() => setUpdateLoading(false));
  };


  return (
    <div className="dialog-overlay">
      <div className="dialog">
        <h2>Manage Slack Notifications</h2>
        {slackWorkspace ? (
          <div className="workspace-info">
            <img
              src={slackWorkspace.teamIcon}
              alt={slackWorkspace.teamName}
              className="workspace-icon"
            />
            <div className="team-meta">
              <h3>{slackWorkspace.teamName}</h3>
              <p>Team ID: {slackWorkspace.teamId}</p>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", margin: "20px 0", height: '110px' }}>
            <CircularProgress
              size={36}
              sx={{
                color: colors.blue[500],
                marginRight: 4,
              }}
            />
            <p>Loading Slack Workspace info...</p>
          </div>
        )}
        {/* Notification Sections Grid */}
        <div className="notification-sections-grid">
          {/* Client Health Alerts */}
          <div className="notification-section" style={{ position: 'relative' }}>
            <div className="section-control">
              <h3>Client Health Alerts</h3>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={notificationSettings.clientHealthAlerts ?? false}
                  onChange={() => handleToggle("clientHealthAlerts")}
                />
                <span className="slider"></span>
              </label>
            </div>
            <div className="grid-list">
              <div className="toggle-item">
                <div className="toggle-header-left">
                  <span className="toggle-title">Critical CRS Changes</span>
                </div>
                <p className="toggle-description">Get notified when client health drops to yellow or red</p>
              </div>
              <div className="toggle-item">
                <div className="toggle-header-left">
                  <span className="toggle-title">Positive Momentum</span>
                </div>
                <p className="toggle-description">Celebrate when clients improve their health status</p>
              </div>
              <div className="toggle-item">
                <div className="toggle-header-left">
                  <span className="toggle-title">Trend Alerts</span>
                </div>
                <p className="toggle-description">Alert when clients show declining patterns over time</p>
              </div>
              <div className="toggle-item">
                <div className="toggle-header-left">
                  <span className="toggle-title">Payment/Billing Alerts</span>
                </div>
                <p className="toggle-description">Notifications for billing status changes</p>
              </div>
            </div>
            {/* Overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'auto',
                borderRadius: '12px',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: '#555',
                  fontSize: 24,
                  opacity: 0.8,
                  fontFamily: "'Albert Sans', sans-serif",
                }}
              >
                Coming Soon...
              </Typography>
            </Box>
          </div>

          {/* Task & Activity Reminders */}
          <div className="notification-section" style={{ position: 'relative' }}>
            <div className="section-control">
              <h3>Task & Activity Reminders</h3>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={notificationSettings.taskReminders ?? false}
                  onChange={() => handleToggle("taskReminders")}
                />
                <span className="slider"></span>
              </label>
            </div>
            <div className="grid-list">
              <div className="toggle-item">
                <div className="toggle-header-left">
                  <span className="toggle-title">Overdue Client Outreach</span>
                </div>
                <p className="toggle-description">Alerts when client contact is overdue</p>
              </div>
              <div className="toggle-item">
                <div className="toggle-header-left">
                  <span className="toggle-title">Task Due Dates</span>
                </div>
                <p className="toggle-description">Daily digest of upcoming and due tasks</p>
              </div>
              <div className="toggle-item">
                <div className="toggle-header-left">
                  <span className="toggle-title">Call Review Reminders</span>
                </div>
                <p className="toggle-description">Notifications for transcript awaiting review</p>
              </div>
              <div className="toggle-item">
                <div className="toggle-header-left">
                  <span className="toggle-title">Follow-up Reminders</span>
                </div>
                <p className="toggle-description">Based on previous interaction notes</p>
              </div>
            </div>
            {/* Overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'auto',
                borderRadius: '12px',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: '#555',
                  fontSize: 24,
                  opacity: 0.8,
                  fontFamily: "'Albert Sans', sans-serif",
                }}
              >
                Coming Soon...
              </Typography>
            </Box>
          </div>

          {/* Daily/Weekly Digests */}
          <div className="notification-section">
            <div className="section-control">
              <h3>Daily/Weekly Digests</h3>
            </div>
            <div className="toggle-list">
              <div className="toggle-item">
                <div className="toggle-header-left">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.morningBriefing ?? false}
                      onChange={(e) => { handleToggle("morningBriefing"); handleToggle("weekInReview", !e.target.checked); }}
                    />
                    <span className="slider"></span>
                  </label>
                  <span className="toggle-title">Morning Briefing <small>(Sent at 6am M-F)</small></span>
                </div>
                <p className="toggle-description-i">Daily summary of clients needing attention</p>
              </div>
              <div className="toggle-item">
                <div className="toggle-header-left">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={notificationSettings.weekInReview ?? false}
                      onChange={(e) => { handleToggle("weekInReview"); handleToggle("morningBriefing", !e.target.checked); }}
                    />
                    <span className="slider"></span>
                  </label>
                  <span className="toggle-title">Week in Review <small>(Sent on Saturdays)</small></span>
                </div>
                <p className="toggle-description-i">Weekly summary of wins, risks, and priorities</p>
              </div>
            </div>
          </div>

          {/* Notification Controls */}
          <div className="notification-section">
            <div className="section-control">
              <h3>Notification Settings</h3>
            </div>

            {/* Channel Selection */}
            <div className="channel-selection">
              <h4>Channel Selection</h4>
              <FormControl sx={{ m: 0.5 }} size="small">
                <Select
                  className="channel-dropdown"
                  value={notificationSettings.channelId || ""}
                  onChange={(e) => setNotificationSettings(prev => ({ ...prev, channelId: e.target.value }))}
                >
                  <MenuItem className="channel-dropdown-item" value="">Select Channel</MenuItem>
                  {slackChannels.length > 0 ? (
                    slackChannels.map((channel) => (
                      <MenuItem className="channel-dropdown-item" key={channel.id} value={channel.id}>
                        {channel?.private ? <LockOutlinedIcon sx={{ width: '18px', marginX: '4px' }} /> : <TagOutlinedIcon sx={{ width: '18px', marginX: '4px' }} />} {channel.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem className="channel-dropdown-item" value="" disabled>No channels available</MenuItem>
                  )}
                </Select>
              </FormControl>
              <p className="channel-description">Select which Slack channel receives notifications</p>
            </div>

            {/* Frequency Settings */}
            {/* <div className="frequency-settings">
              <h4>Frequency Settings</h4>
              <div className="frequency-radio-group" role="radiogroup" aria-label="Notification Frequency">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="frequency"
                    value="realtime"
                    checked={notificationSettings.frequency === "realtime"}
                    onChange={() => setNotificationSettings(prev => ({ ...prev, frequency: "realtime" }))}
                  />
                  Real-time
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="frequency"
                    value="hourly"
                    checked={notificationSettings.frequency === "hourly"}
                    onChange={() => setNotificationSettings(prev => ({ ...prev, frequency: "hourly" }))}
                  />
                  Hourly digest
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="frequency"
                    value="daily"
                    checked={notificationSettings.frequency === "daily"}
                    onChange={() => setNotificationSettings(prev => ({ ...prev, frequency: "daily" }))}
                  />
                  Daily digest
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="frequency"
                    value="weekly"
                    checked={notificationSettings.frequency === "weekly"}
                    onChange={() => setNotificationSettings(prev => ({ ...prev, frequency: "weekly" }))}
                  />
                  Weekly digest
                </label>
              </div>
            </div> */}
          </div>
        </div>
        <div className="dialog-actions">
          <Box
            sx={{ m: 1, position: 'relative' }}>
            <Button
              className="update-button"
              sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
              onClick={updateNotificationSettings}
              variant="outlined"
              color="success"
              size="large"
              disabled={updateLoading}
            >
              Update
            </Button>
            {updateLoading && (
              <CircularProgress
                size={24}
                sx={{
                  color: colors.green[500],
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
          <Box
            sx={{ m: 1, position: 'relative' }}>
            <Button
              className="close-button"
              sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
              onClick={closeDialog}
              variant="outlined"
              color="error"
              size="large"
            >
              Close
            </Button>
          </Box>
        </div>
      </div>
    </div>
  )
};

export default SlackModal;